/**
 * UC2 launch constants (client spec `2026-10-01-UC2_LAUNCH_SPEC.md` §2/§3, design §3) —
 * shared between `Uc2.tsx`, its panels, `uc2Paint.ts` and the tests, split out of the
 * component modules so each of those stays a components-only file for React Fast Refresh.
 *
 * Every palette is fixed here rather than in a component, for the UC1 reason
 * (`uc1Constants.ts`): the chip, the map and the legend all read the same entry, so a
 * reader who has learned "green is positive change" never has to relearn it.
 */
import { lighten } from './uc1Constants';

/** The three launch classes, in legend order. */
export const UC2_CATEGORIES = ['positive', 'no_pattern', 'not_assessed'] as const;

/**
 * The pattern chip's palette. `not_assessed` is not a colour — it is the hatch
 * (`resultUnits.ts`), drawn over `NEUTRAL_COLOR`, so it has no entry here.
 */
export const UC2_CATEGORY_COLORS: Record<string, string> = {
  positive: '#2a9d4a',
  no_pattern: '#9aa5a0',
};

/**
 * "Nothing to say about this unit on this chip", and the ground a hatch is drawn over.
 *
 * Client round 3 Oct 2026: a clearly visible mid grey, for the UC1 reason
 * (`uc1Constants.ts`) — the old `#d9ded9` read as a hole in the map rather than as a
 * class. It is the same ink as `UC2_REST_COLOR`, which is the same statement in a chart.
 */
export const NEUTRAL_COLOR = '#9aa5a0';

/**
 * `#uc2Chips` — exclusive, `pattern` by default, `greening` greyed until the gate passes.
 *
 * Client round 2 Oct 2026: **no `similar` chip**. It was the one chip that painted nothing
 * until a watershed was clicked, and what it painted then — three neighbours in a practice
 * colour — was the Similar tab's own subject said a second time, in a palette the reader
 * had just learned meant adoption. The alike watersheds live on that tab and nowhere else.
 */
export const UC2_CHIPS = [
  'pattern',
  'landcover',
  'cropland',
  'rainfall',
  'practices',
  'greening',
] as const;
export type Uc2Chip = (typeof UC2_CHIPS)[number];

/**
 * The chips that have nothing to say about a country.
 *
 * **Empty since the client round of 3 Oct 2026.** Land-cover change and greening used to
 * be greyed here: both draw a raster rather than a roll-up, and the pane had no country
 * reading to put under them. They now draw the very same raster at country level, with
 * the hairline boundary layer of the four countries over it, and `#uc2UnitsChart` says
 * the country part — each country's mean of its own watersheds' change shares and
 * vegetation trend, derived on the client from the watershed rows the pane already holds,
 * exactly as the three evidence chips derive their country shares.
 *
 * The constant stays, with the greying it drives, so a future chip that really is a
 * watershed-only reading needs one entry here and no component change.
 */
export const UC2_WATERSHED_ONLY_CHIPS: readonly Uc2Chip[] = [];

/** `#intelLevel` on UC2 — the same two levels UC1 switches between. */
export const UC2_LEVELS = ['watershed', 'admin0'] as const;
export type Uc2Level = (typeof UC2_LEVELS)[number];

/** `#uc2Tabs` — the five second-level tabs, Overview first. */
export const UC2_TABS = ['overview', 'trends', 'practices', 'similar', 'method'] as const;
export type Uc2Tab = (typeof UC2_TABS)[number];

/** The stored chip, or `'pattern'` when the store holds an id this build does not know. */
export function normaliseChip(chip: string): Uc2Chip {
  return (UC2_CHIPS as readonly string[]).includes(chip) ? (chip as Uc2Chip) : 'pattern';
}

/** `#uc2Practice` — the four survey practice groups, in the order the selector prints them. */
export const UC2_PRACTICE_GROUPS = [
  'agroforestry',
  'agroecological',
  'inputs',
  'agropastoral',
] as const;
export type Uc2Practice = (typeof UC2_PRACTICE_GROUPS)[number];

/** The definition's `adoption.default_group` (backend task-1 report §2.2). */
export const UC2_DEFAULT_PRACTICE: Uc2Practice = 'agroforestry';

/** The stored practice, or the default when the store holds a group this build does not know. */
export function normalisePractice(group: string): Uc2Practice {
  return (UC2_PRACTICE_GROUPS as readonly string[]).includes(group)
    ? (group as Uc2Practice)
    : UC2_DEFAULT_PRACTICE;
}

/**
 * The stability band, in percent — the same `stability_band` the engine classifies with
 * (backend §5). The pane never re-classifies anything with it; it is used only to word the
 * cropland and rainfall captions the same way the map is coloured, so a caption can never
 * say "stable" about a watershed the map painted as growing.
 */
export const STABILITY_BAND_PCT = 5;

/** Cropland change: one green family, declining (lightest) → stable → growing. */
export const CROPLAND_BASE = '#2a9d4a';
/** Rainfall tendency: one blue family, drier (lightest) → stable → wetter. */
export const RAINFALL_BASE = '#2166ac';
/** Practice adoption: one purple family, low → mid → high. */
export const PRACTICE_BASE = '#6a51a3';

/** The three steps of a family, lightest first — the UC1 helper, so they cannot drift. */
export function familySteps(base: string): [string, string, string] {
  return [lighten(base, 0.62), lighten(base, 0.31), base];
}

/**
 * The country choropleth's own family: the share of a country's watersheds with a positive
 * pattern, in three steps of the positive green (2 Oct chart round, design §2.1).
 */
export const POSITIVE_BASE = UC2_CATEGORY_COLORS.positive!;

/** Share-of-watersheds step boundaries, in percent: under a third, up to two thirds, above. */
export const POSITIVE_BREAKS: [number, number] = [100 / 3, 200 / 3];

/** Which of the three share steps a percentage falls in, or `null` for no share at all. */
export function positiveStep(pct: number | null | undefined): 0 | 1 | 2 | null {
  if (typeof pct !== 'number' || !Number.isFinite(pct)) return null;
  if (pct < POSITIVE_BREAKS[0]) return 0;
  if (pct < POSITIVE_BREAKS[1]) return 1;
  return 2;
}

/** Adoption step boundaries, in percent: under a quarter, up to a half, above. */
export const PRACTICE_BREAKS: [number, number] = [25, 50];

/** Which of the three adoption steps a percentage falls in. */
export function practiceStep(pct: number): 0 | 1 | 2 {
  if (pct < PRACTICE_BREAKS[0]) return 0;
  if (pct < PRACTICE_BREAKS[1]) return 1;
  return 2;
}

/**
 * The definition's `adoption_gap` (`backend/apps/intelligence/defaults.py`, 15 points): how
 * far behind a comparable has to sit before the engine calls the pair a learning
 * opportunity.
 *
 * The engine's own `learning` flag is still what the cards read, and it still wins — this
 * is only the mirror image the payload carries no flag for ("the other side reports more,
 * by at least as much"), so that the plain note under a card can tell that case apart from
 * two watersheds at a similar level. A curator who moves the config moves the flag; this
 * number would then be stale for that one branch only, which is why nothing is classified
 * with it.
 */
export const UC2_ADOPTION_GAP_POINTS = 15;

/** A learning opportunity's accent — warm, so it reads as "look here" beside the family. */
export const SIMILAR_OPPORTUNITY_COLOR = '#e07b39';

/** The selected watershed's outline, on every chip (design §3's "outlined on every chip"). */
export const SELECTED_OUTLINE_COLOR = '#2B6CB0';

/**
 * The land-cover change chip's own published COG (2 Oct chart round, design §2.4): one
 * categorical raster of the 2017→2024 change classes, drawn in place of the swipe. Its
 * legend is the catalogue's own `legend` for this layer — the five classes below are only
 * the last resort for a `/layers` response that carries none. `id` is the locale suffix,
 * `intel.uc2.lcChangeClass.<id>`.
 */
export const UC2_LC_CHANGE_LAYER = 'intel_uc2_lc_change';

export const UC2_LC_CHANGE_COLORS = {
  crop_gain: '#1a9850',
  crop_loss: '#d73027',
  veg_gain: '#66bd63',
  veg_loss: '#f46d43',
  none: '#e8eae6',
} as const;

export const UC2_LC_CHANGE_FALLBACK_CLASSES: { id: string; color: string }[] = (
  ['crop_gain', 'crop_loss', 'veg_gain', 'veg_loss', 'none'] as const
).map((id) => ({ id, color: UC2_LC_CHANGE_COLORS[id] }));

/** The greening chip's already-published trend COG, switched on once the gate passes. */
export const UC2_GREENING_LAYER = 'intel_uc2_ndvi_trend';

/**
 * The greening legend's last resort: the two ends of the trend, in words, for a catalogue
 * that has no `legend` and no `ramp` on `intel_uc2_ndvi_trend` (an older server, or a
 * `/layers` response that has not landed yet). The chip's real legend is the layer's own
 * one, read exactly as the Layers pane reads it — this is only so the reader is never
 * shown a colour strip with nothing said about it. `id` is the locale suffix,
 * `intel.uc2.legend.greening.<id>`.
 */
export const UC2_GREENING_FALLBACK_CLASSES: { id: string; color: string }[] = [
  { id: 'up', color: '#2a9d4a' },
  { id: 'down', color: '#a1662f' },
];

/**
 * The programme extent the land-cover chip fits to when no watershed is selected.
 *
 * Re-exported from the store rather than spelled again: `#intelReset` fits to the same box
 * (client round 2 Oct 2026), and two copies of four numbers that must agree is one copy
 * too many. `features/map/panes.ts` holds the Leaflet form of it as `HOME_BOUNDS`.
 */
export { PROGRAMME_BOUNDS } from '../../lib/state';

/**
 * The scopes the 2 Oct tab-analytics round precomputes (design §Summary additions): the
 * whole programme, and one per country. A tab reads the programme at watershed level and
 * one country at country level, so the same block serves both without a second request.
 *
 * **Which countries is the run's business, not this file's**: the select is built from the
 * keys `by_class_series` actually carries, so a run that gains or loses a country needs no
 * frontend change. Only the label map below is spelled out, and it falls back to the code.
 */
export const UC2_PROGRAMME_SCOPE = 'programme';

/** The locale key for a scope's name — `country.*` is keyed on ISO-3, the scopes on ISO-2. */
export const UC2_SCOPE_ISO3: Record<string, string> = {
  BF: 'BFA',
  ML: 'MLI',
  NE: 'NER',
  NG: 'NGA',
};

/**
 * Which scope the three analytic tabs read: the programme at watershed level; at country
 * level the country the reader has actually clicked (its detail says `admin0`, which is
 * the only thing that proves it is one), else whichever of the run's own scopes
 * `#uc2Scope` holds, else the first the run serves.
 *
 * A clicked country the run has no slice for is returned unchanged — the panels then print
 * their "nothing here yet" note, which is the honest answer; silently falling back to
 * another country's numbers would label one country's data with another's name.
 */
export function uc2Scope(
  level: Uc2Level,
  selectedCountry: string | null,
  chosen: string | null,
  options: readonly string[],
): string {
  if (level !== 'admin0') return UC2_PROGRAMME_SCOPE;
  if (selectedCountry) return selectedCountry;
  if (chosen && options.includes(chosen)) return chosen;
  return options[0] ?? '';
}

/**
 * Whether a watershed belongs to a scope. The programme holds every unit; a country holds
 * the watersheds whose id starts with its code (`BFWS01` sits under `BF`) — the same id
 * convention the chips fall back to when a row carries no `country`.
 */
export function unitInScope(unitId: string, scope: string): boolean {
  return scope === UC2_PROGRAMME_SCOPE || unitId.startsWith(scope);
}

/** The "everything that is not positive change" grey, shared by every block that pairs
 * the two classes — one ink for one meaning, like every other colour in this file. */
export const UC2_REST_COLOR = '#9aa5a0';

/**
 * One muted ink per unit, for the Trends tab's per-unit line charts (client round 3 Oct
 * 2026): eleven watersheds, or four countries, on one plot. Muted on purpose — none of
 * these lines is a verdict, and the one line the reader has focused is told apart by
 * being drawn bold while the rest are thinned, not by being the only saturated colour.
 * Cycles if a run ever carries more units than there are inks here.
 */
export const UC2_UNIT_LINE_COLORS = [
  '#4c7a8a',
  '#8a6f4c',
  '#6b8a4c',
  '#8a4c6b',
  '#4c5a8a',
  '#7a8a4c',
  '#8a5a4c',
  '#4c8a7a',
  '#6b4c8a',
  '#8a7a4c',
  '#5a6b7a',
] as const;

/**
 * Highest first, with the unmeasured last. `(b ?? -Infinity) - (a ?? -Infinity)` is `NaN`
 * for two unmeasured rows, which leaves their order up to the engine's sort — so the
 * comparison is spelled out instead.
 */
export function byValueDesc(a: number | null | undefined, b: number | null | undefined): number {
  const left = typeof a === 'number' && Number.isFinite(a) ? a : null;
  const right = typeof b === 'number' && Number.isFinite(b) ? b : null;
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return right - left;
}

/* ------------------------------------------------------------------------------------ *
 * Scaling round, 3 October 2026 — `#uc2Drivers` and `#uc2Scaling`.
 * ------------------------------------------------------------------------------------ */

/**
 * The drivers block's two bar inks. "Ok" is the very green the pattern chip paints a
 * positive unit with, so a green bar here and a green unit on the map mean one thing; the
 * rest is the same neutral every other block uses for "not that".
 *
 * A `null` check is **not** a third colour: it is the hatch over the neutral, exactly as
 * a not-assessed unit is drawn on the map.
 */
export const DRIVER_OK_COLOR = UC2_CATEGORY_COLORS.positive!;
export const DRIVER_NOT_COLOR = UC2_REST_COLOR;

/** The ±band strip behind the signed bars — a ground, not a line, so it never reads as a value. */
export const DRIVER_BAND_COLOR = '#e3e8e3';

/** The three verdicts a scaling candidate can carry, strongest first. */
export const UC2_VERDICTS = ['strong', 'possible', 'weak'] as const;
export type Uc2Verdict = (typeof UC2_VERDICTS)[number];

/** Green, amber, grey — the badge inks, in the same order. */
export const UC2_VERDICT_COLORS: Record<Uc2Verdict, string> = {
  strong: '#2a9d4a',
  possible: '#d98c1f',
  weak: '#9aa5a0',
};

/**
 * The run's own verdict, or `'weak'` for a row that carries a word this build does not
 * know. Never re-derived from the score: the thresholds are the definition's, and a badge
 * computed here would disagree with the engine the day a curator moves them.
 */
export function normaliseVerdict(verdict: string | null | undefined): Uc2Verdict {
  return (UC2_VERDICTS as readonly string[]).includes(verdict ?? '')
    ? (verdict as Uc2Verdict)
    : 'weak';
}

/** A score clamped into the 0–1 the bar draws, or `null` when the row carries none. */
export function clampScore(score: number | null | undefined): number | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null;
  return Math.min(1, Math.max(0, score));
}

/**
 * Highest score first, ties broken by name so the list is stable between renders of the
 * same payload. A row with no score sorts last, next to the others that carry none.
 */
export function rankScaling<T extends { name: string; score?: number | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const order = byValueDesc(a.score, b.score);
    return order !== 0 ? order : a.name.localeCompare(b.name);
  });
}
